import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Avatar,
  CircularProgress,
} from '@mui/material';
import { Description } from '@mui/icons-material';
import { AppLayout } from '../components/layout/AppLayout';
import { BackButton } from '../components/common/BackButton';
import { useAuth } from '../hooks/useAuth';
import { useClinic } from '../hooks/useClinic';
import { createPaperRequest, getPaperRequests } from '../services/requestsService';
import { SuccessPopup } from '../components/common/SuccessPopup';
import { useNavigate } from 'react-router-dom';
import { getUserPhotoUrl } from '../utils/getUserPhotoUrl';

export const PaperRequestPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { branches } = useClinic();

  const [inn, setInn] = useState('');
  const [year, setYear] = useState('');
  const [city, setCity] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasPreviousRequest, setHasPreviousRequest] = useState(false);
  const [userPhotoUrl, setUserPhotoUrl] = useState<string | null>(null);

  const hasPatientCard = !!user?.patientClinicId;
  const currentBranch = useMemo(
    () => branches.find((b) => b.id === user?.branchId),
    [branches, user?.branchId]
  );
  const branchName = currentBranch?.name || 'Филиал';
  const userName = user?.name || 'Пользователь';
  const userInitials = user?.name
    ?.split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase() || '?';

  const workSchedule = useMemo(() => {
    if (currentBranch?.workSchedule) {
      return currentBranch.workSchedule;
    }
    return null;
  }, [currentBranch?.workSchedule]);

  const isFormFilled = inn.trim() && year.trim() && city.trim();

  useEffect(() => {
    const fetchPaperRequests = async () => {
      try {
        const response = await getPaperRequests(true);
        setHasPreviousRequest(response.requests.length > 0);
      } catch (err) {
        // Если ошибка, остаемся с текущим состоянием
        console.error('Ошибка при загрузке справок:', err);
      }
    };

    const photoUrl = getUserPhotoUrl();
    setUserPhotoUrl(photoUrl);

    fetchPaperRequests();
  }, []);

  const handleSubmit = async () => {
    if (!inn.trim() || !year.trim() || !city.trim()) {
      setError('Заполните все поля');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    const comment = `ИНН: ${inn}\nГод: ${year}\nГород: ${city}`;

    try {
      await createPaperRequest({ comment });
      setShowSuccessModal(true);
    } catch (err: any) {
      setError(err.message || 'Ошибка при отправке заявки');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!hasPatientCard) {
    return (
      <AppLayout title="Заказать справку">
        <Box sx={{ p: 2, textAlign: 'center' }}>
          <Typography color="error">
            Для заказа справки необходимо привязать карту клиники
          </Typography>
          <Button
            variant="contained"
            sx={{ mt: 2 }}
            onClick={() => navigate('/profile?tab=data')}
          >
            Привязать карту
          </Button>
        </Box>
      </AppLayout>
    );
  }

  return (
    <AppLayout title="Заказать справку">
      <Box
        sx={{
          backgroundColor: '#F5F6F8',
          minHeight: '100vh',
          pb: 8,
        }}
      >
        <BackButton onClick={() => navigate('/cabinet')} />

        {hasPreviousRequest ? (
          <>
            <Typography
              sx={{
                fontSize: 20,
                fontWeight: 600,
                color: '#1C1C1E',
                mb: 1,
                lineHeight: 1.1,
                fontFamily: 'TikTok Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
              }}
            >
              Мы видим вашу предыдущую заявку на справку
            </Typography>

            <Typography
              sx={{
                fontSize: 16,
                fontWeight: 500,
                color: '#6B6B6F',
                mb: 2,
                lineHeight: 1.3,
                fontFamily: 'TikTok Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
              }}
            >
              Если нужно что-то изменить (ИНН, год<br />
              или город) - заполните любое из этих<br />
              полей заново
            </Typography>
          </>
        ) : (
          <>
            <Typography
              sx={{
                fontSize: 36,
                fontWeight: 600,
                color: '#1C1C1E',
                mt: 2,
                mb: 1,
                lineHeight: 1.1,
                fontFamily: 'TikTok Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
              }}
            >
              Заказать справку
            </Typography>

            <Typography
              sx={{
                fontSize: 16,
                fontWeight: 500,
                color: '#6B6B6F',
                mb: 2,
                lineHeight: 1.2,
                fontFamily: 'TikTok Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
              }}
            >
              После отправки данных с вами<br />
              свяжется менеджер
            </Typography>
          </>
        )}

        {/* User Card */}
        <Box
          sx={{
            backgroundColor: '#FFFFFF',
            borderRadius: '24px',
            padding: '20px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mt: 2,
            mb: 3,
            boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
          }}
        >
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flex: 1 }}>
            <Avatar
              src={userPhotoUrl || undefined}
              sx={{
                width: 52,
                height: 52,
                backgroundColor: '#1677FF',
                color: '#FFFFFF',
                fontSize: 18,
                fontWeight: 600,
              }}
            >
              {userInitials}
            </Avatar>
            <Box>
              <Typography
                sx={{
                  fontSize: 17,
                  fontWeight: 600,
                  color: '#1C1C1E',
                  fontFamily: 'TikTok Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                }}
              >
                {userName}
              </Typography>
              <Typography
                sx={{
                  fontSize: 13,
                  color: '#8E8E93',
                  fontFamily: 'TikTok Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                }}
              >
                {branchName}
              </Typography>
            </Box>
          </Box>

          {/* Document Icon Button */}
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: '50%',
              backgroundColor: '#1677FF',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <Description sx={{ fontSize: 22, color: '#FFFFFF' }} />
          </Box>
        </Box>

        {/* Input Fields */}
        <TextField
          fullWidth
          placeholder="ИНН"
          value={inn}
          onChange={(e) => setInn(e.target.value)}
          inputProps={{
            style: {
              fontSize: '15px',
              fontWeight: 400,
            },
          }}
          sx={{
            mb: 1.5,
            '& .MuiOutlinedInput-root': {
              height: '48px',
              borderRadius: '22px',
              backgroundColor: '#F5F6F8',
              padding: '0 16px',
              '& fieldset': {
                borderColor: 'rgba(33, 33, 33, 0.3)',
                borderWidth: '1px',
              },
              '&:hover fieldset': {
                borderColor: 'rgba(33, 33, 33, 0.3)',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#1677FF',
                borderWidth: '1px',
              },
            },
            '& .MuiOutlinedInput-input': {
              color: 'rgba(33, 33, 33, 0.7)',
            },
            '& .MuiOutlinedInput-input::placeholder': {
              color: 'rgba(33, 33, 33, 0.7)',
              opacity: 1,
            },
          }}
        />

        <TextField
          fullWidth
          placeholder="Год"
          value={year}
          onChange={(e) => setYear(e.target.value)}
          inputProps={{
            style: {
              fontSize: '15px',
              fontWeight: 400,
            },
          }}
          sx={{
            mb: 1.5,
            '& .MuiOutlinedInput-root': {
              height: '48px',
              borderRadius: '22px',
              backgroundColor: '#F5F6F8',
              padding: '0 16px',
              '& fieldset': {
                borderColor: 'rgba(33, 33, 33, 0.3)',
                borderWidth: '1px',
              },
              '&:hover fieldset': {
                borderColor: 'rgba(33, 33, 33, 0.3)',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#1677FF',
                borderWidth: '1px',
              },
            },
            '& .MuiOutlinedInput-input': {
              color: 'rgba(33, 33, 33, 0.7)',
            },
            '& .MuiOutlinedInput-input::placeholder': {
              color: 'rgba(33, 33, 33, 0.7)',
              opacity: 1,
            },
          }}
        />

        <TextField
          fullWidth
          placeholder="Город"
          value={city}
          onChange={(e) => setCity(e.target.value)}
          inputProps={{
            style: {
              fontSize: '15px',
              fontWeight: 400,
            },
          }}
          sx={{
            mb: 3,
            '& .MuiOutlinedInput-root': {
              height: '48px',
              borderRadius: '22px',
              backgroundColor: '#F5F6F8',
              padding: '0 16px',
              '& fieldset': {
                borderColor: 'rgba(33, 33, 33, 0.3)',
                borderWidth: '1px',
              },
              '&:hover fieldset': {
                borderColor: 'rgba(33, 33, 33, 0.3)',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#1677FF',
                borderWidth: '1px',
              },
            },
            '& .MuiOutlinedInput-input': {
              color: 'rgba(33, 33, 33, 0.7)',
            },
            '& .MuiOutlinedInput-input::placeholder': {
              color: 'rgba(33, 33, 33, 0.7)',
              opacity: 1,
            },
          }}
        />

        {error && (
          <Typography
            sx={{
              fontSize: 14,
              color: '#FF3B30',
              mb: 2,
              textAlign: 'center',
            }}
          >
            {error}
          </Typography>
        )}

        {/* Submit Button */}
        <Button
          fullWidth
          variant="contained"
          disabled={!isFormFilled || isSubmitting}
          onClick={handleSubmit}
          sx={{
            height: '52px',
            borderRadius: '16px',
            fontWeight: 600,
            fontSize: 16,
            textTransform: 'none',
            boxShadow: 'none',
            backgroundColor: isFormFilled ? '#1677FF' : '#A9C7F5',
            color: '#FFFFFF',
            transition: 'background-color 0.2s, transform 0.15s',
            '&:active': {
              transform: isFormFilled ? 'scale(0.97)' : 'scale(1)',
            },
            '&:hover': {
              backgroundColor: isFormFilled ? '#0055CC' : '#A9C7F5',
            },
            '&:disabled': {
              backgroundColor: '#A9C7F5',
              color: '#FFFFFF',
            },
            fontFamily: 'TikTok Sans, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
          }}
        >
          {isSubmitting ? (
            <CircularProgress size={24} sx={{ color: '#FFF' }} />
          ) : (
            'Отправить'
          )}
        </Button>

        {/* Success Modal */}
        <SuccessPopup
          open={showSuccessModal}
          onClose={() => {
            setShowSuccessModal(false);
            navigate('/cabinet');
          }}
          clinicName={branchName}
          workSchedule={workSchedule}
        />
      </Box>
    </AppLayout>
  );
};